import { transactionAllocations } from "../../../lib/allocations";
import { Suspense, type ReactNode } from "react";
import { ArrowLeft, ArrowUpRight, ChartNoAxesColumn, ChevronRight, PieChart, PiggyBank } from "lucide-react";
import type { Budget, BudgetOwner, Transaction } from "../../../types";
import type { OverviewProps } from "../Overview";
import { money, palette } from "../../../lib/finance";
import { resolveBudget } from "../../../lib/budgets";
import Empty from "../../../components/Empty";
import { Private, useMask } from "../../../components/Privacy";
import { lazyScreen } from "../../../app/lazy-screen";

const CategoryBarChart = lazyScreen(() => import("../charts/CategoryBarChart"), { compact: true });

/** "Where it went": this month's spending by category, or by subcategory within one. */
export default function CategoryBreakdown({
  chart, expenses, budgets, budgetOwner, month, collapsed, collapseButton,
}: {
  chart: OverviewProps["chart"];
  expenses: Transaction[];
  budgets: Budget[];
  budgetOwner: BudgetOwner;
  month: string;
  collapsed: boolean;
  /** The module's collapse toggle, drawn by the caller so every module's matches. */
  collapseButton: ReactNode;
}) {
  const {
    categories, chartTotal, chartCategory, setChartCategory,
    categoryChartStyle, setCategoryChartStyle, openCategory,
  } = chart;
  const mask = useMask();
  return (
    <section className={`panel category-panel overview-module${collapsed ? " is-collapsed" : ""}`}>
      <div className="section-heading">
        <div>
          <h2>{chartCategory ?? "Where it went"}</h2>
          <p>
            {chartCategory
              ? "Spending by subcategory"
              : "Spending by category"}
          </p>
        </div>
        <div className="category-chart-toolbar">
          <span>
            {categories.length}{" "}
            {chartCategory ? "subcategories" : "categories"}
          </span>
          <button aria-label="Show donut chart" title="Donut chart" aria-pressed={categoryChartStyle === "donut"} onClick={() => setCategoryChartStyle("donut")}><PieChart size={15} /></button>
          <button aria-label="Show bar chart" title="Bar chart" aria-pressed={categoryChartStyle === "bars"} onClick={() => setCategoryChartStyle("bars")}><ChartNoAxesColumn size={15} /></button>
          {collapseButton}
        </div>
      </div>
      {chartCategory !== null && (
        <button
          className="chart-back text-button"
          onClick={() => setChartCategory(null)}
        >
          <ArrowLeft size={16} /> All categories
        </button>
      )}
      {categories.length > 0 && chartTotal !== null ? (
        <>
          {categoryChartStyle === "donut" ? <div className="donut-area">
            <div className="donut">
              <svg
                className="ring-svg"
                viewBox="0 0 190 190"
                aria-label={
                  chartCategory
                    ? `${chartCategory} subcategories`
                    : "Spending categories"
                }
              >
                {(() => {
                  let offset = 0;
                  const circumference = 2 * Math.PI * 83;
                  return categories.map(([c, v], i) => {
                    const length =
                      (v / (chartTotal || 1)) * circumference;
                    const dashOffset = -offset;
                    offset += length;
                    return (
                      <circle
                        key={c}
                        cx="95"
                        cy="95"
                        r="83"
                        fill="none"
                        stroke={palette[i % palette.length]}
                        strokeWidth="17"
                        strokeDasharray={`${Math.max(0, length - 2)} ${circumference - Math.max(0, length - 2)}`}
                        strokeDashoffset={dashOffset}
                        tabIndex={0}
                        role="button"
                        aria-label={`${c}: ${mask(money(v))}. ${chartCategory === null && expenses.some((t) => transactionAllocations(t).some(p => p.category === c && p.subcategory)) ? "Explore subcategories" : "View transactions"}.`}
                        onClick={() => openCategory(c)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" ||
                            e.key === " "
                          ) {
                            e.preventDefault();
                            openCategory(c);
                          }
                        }}
                      >
                        <title>
                          {c}: {mask(money(v))}
                        </title>
                      </circle>
                    );
                  });
                })()}
              </svg>
              <div>
                <span>THIS MONTH</span>
                <strong>
                  <Private>{money(chartTotal, "MVR", 0).replace(
                    " MVR",
                    "",
                  )}</Private>
                </strong>
                <small>MVR spent</small>
              </div>
            </div>
            <div className="chart-note">
              <span className="dot mint" />
              {categories[0][0]} leads this month
              <small>
                {Math.round(
                  (categories[0][1] / (chartTotal || 1)) *
                    100,
                )}
                %{" "}
                {chartCategory
                  ? `of ${chartCategory.toLowerCase()}`
                  : "of your spending"}
              </small>
            </div>
          </div> : <Suspense fallback={<div className="chart-loading" role="status">Loading chart…</div>}><CategoryBarChart categories={categories} onSelect={openCategory} /></Suspense>}
          {categoryChartStyle === "donut" && <div className="category-list">
            {categories.map(([c, v], i) => {
              const categoryBudget =
                chartCategory === null
                  ? resolveBudget(budgets, budgetOwner, c, month)
                  : undefined;
              return (
              <button key={c} onClick={() => openCategory(c)}>
                <span
                  className="dot"
                  style={{
                    background: palette[i % palette.length],
                  }}
                />
                <span className="category-name">
                  <span className="category-name-text">{c}</span>
                  {categoryBudget && categoryBudget.amount !== null && (
                    <PiggyBank
                      size={13}
                      className={
                        "category-budget-dot" +
                        (v > categoryBudget.amount ? " over" : "")
                      }
                      role="img"
                      aria-label={`${budgetOwner} budget: ${money(categoryBudget.amount, categoryBudget.currency)}`}
                    >
                      <title>{`${budgetOwner} budget: ${money(categoryBudget.amount, categoryBudget.currency)}`}</title>
                    </PiggyBank>
                  )}
                </span>
                <span className="category-percent">
                  {Math.round((v / (chartTotal || 1)) * 100)}%
                </span>
                <strong>
                  <Private>{money(v, "MVR", 0).replace(" MVR", "")}</Private>
                </strong>
                <ChevronRight size={14} />
              </button>
              );
            })}
          </div>}
          <p className="panel-footnote">
            {chartCategory
              ? "Tap a subcategory to see its transactions"
              : "Tap a category to explore its breakdown"}{" "}
            <ArrowUpRight size={12} />
          </p>
        </>
      ) : (
        <Empty
          title={
            chartTotal === null
              ? "Conversion unavailable"
              : "A fresh page"
          }
          description={
            chartTotal === null
              ? "An exchange rate is missing, so this month’s spending can’t be totalled into one chart."
              : "No expenses recorded for this month and profile."
          }
        />
      )}
    </section>
  );
}
